import { FC, ReactNode, useState } from "react";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import GridOnIcon from "@mui/icons-material/GridOn";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import { IconButton, ListItemIcon, ListItemText, MenuItem, MenuList, Popover, Tooltip } from "@mui/material";
import { ToolbarButton } from "@mui/x-data-grid";

export type ExportFormat = "pdf" | "csv" | "xlsx";

// Menu order is fixed, regardless of the order formats are passed in
const EXPORT_FORMAT_OPTIONS: { format: ExportFormat; label: string; Icon: typeof FileDownloadIcon }[] = [
  { format: "pdf", label: "PDF", Icon: PictureAsPdfIcon },
  { format: "xlsx", label: "Excel", Icon: GridOnIcon },
  { format: "csv", label: "CSV", Icon: TableViewIcon },
];

export type ExportMenuProps = {
  disabled?: boolean;
  formats?: ExportFormat[];
  icon?: ReactNode;
  // Renders the trigger as a DataGrid ToolbarButton so it participates in the grid toolbar's keyboard navigation
  inGridToolbar?: boolean;
  onExport: (format: ExportFormat) => void;
};

export const ExportMenu: FC<ExportMenuProps> = ({
  disabled = false,
  formats = ["csv", "xlsx"],
  icon = <FileDownloadIcon fontSize="small" />,
  inGridToolbar = false,
  onExport,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (format: ExportFormat) => {
    setAnchorEl(null);
    onExport(format);
  };

  const triggerProps = {
    "aria-label": "Download as...",
    disabled,
    onClick: (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget),
  };

  return (
    <>
      <Tooltip title="Download as...">
        {/* span keeps the tooltip working while the button is disabled */}
        <span>
          {inGridToolbar ? (
            <ToolbarButton {...triggerProps}>{icon}</ToolbarButton>
          ) : (
            <IconButton {...triggerProps} size="small">
              {icon}
            </IconButton>
          )}
        </span>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuList sx={{ minWidth: 120 }}>
          {EXPORT_FORMAT_OPTIONS.filter(({ format }) => formats.includes(format)).map(({ format, label, Icon }) => (
            <MenuItem key={format} onClick={() => handleClick(format)}>
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
              <ListItemText
                slotProps={{
                  primary: {
                    variant: "body1",
                    textAlign: "left",
                  },
                }}
              >
                {label}
              </ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
};
